import React, { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
} from '@mui/material';
import AtomicButton from '../../../../atomic/button/Button';
import ErrorFormAlert from '../../ErrorForm';
import { statusTypeLabel, statusTypeColor } from '../constants';
import { SectionCard } from './SectionCard';
import styles from './styles.module.css';
import variables from '../../../../utils/assets/_variables.scss';
import { getManagingBranchNameForApi } from '../../../../utils/managingBranchName';
import {
  storeManagementMenuPaperSx,
  storeManagementMenuItemPlainSx,
  storeManagementOutlinedSelectSx,
} from '../../../../utils/storeManagementSelectSx';
import {
  mapRegionalProductUpdateErrorsToForm,
  PRODUCTS_LIST_STATUS_TYPE_VALUES,
} from '../../../../context-api/api-services/Products.service';
import { useKitchenProductsData } from '../../../../context-api/kitchen-products/KitchenProductsDataContext';

const statusSectionCardStyle = {
  border: `2px solid ${variables.backgroundDarkBlue}`,
};

const chipSx = (typeColor) => ({
  border: `1px solid ${typeColor}`,
  background: typeColor,
  color: 'white',
  height: '26px',
  fontSize: '12px',
  fontFamily: 'Inter Medium',
  borderRadius: '6px',
  fontWeight: 600,
  '& .MuiChip-label': {
    px: 1.25,
  },
});

/** Normalize API `status_type` for comparisons / enum matching. */
const normalizeStatusType = (value) => String(value ?? '').trim().toLowerCase();

function coerceToAllowedStatusType(raw) {
  const n = normalizeStatusType(raw);
  return PRODUCTS_LIST_STATUS_TYPE_VALUES.includes(n)
    ? n
    : PRODUCTS_LIST_STATUS_TYPE_VALUES[0];
}

const STATUS_NOTES_MAX = 500;

export const StatusUpdateSection = ({ onRefresh, product }) => {
  const { regionalUpdateProductStatus, regionalUpdateProductStatusData } = useKitchenProductsData();
  const statusUpdateLoading = regionalUpdateProductStatusData.loading;
  const [selectedStatusType, setSelectedStatusType] = useState(
    PRODUCTS_LIST_STATUS_TYPE_VALUES[0]
  );
  const [statusNotes, setStatusNotes] = useState('');
  const [formErrors, setFormErrors] = useState([]);
  const [successOpen, setSuccessOpen] = useState(false);

  useEffect(() => {
    setSelectedStatusType(coerceToAllowedStatusType(product?.status_type));
    setStatusNotes(String(product?.status_notes ?? ''));
    setFormErrors([]);
  }, [product]);

  const normalizedWorkflow = normalizeStatusType(product?.status_type);
  const typeLabel = statusTypeLabel[normalizedWorkflow] || statusTypeLabel[selectedStatusType] || 'Status';
  const typeColor = statusTypeColor[normalizedWorkflow] || statusTypeColor[selectedStatusType] || '#999';

  const hasIdentity = Boolean(
    String(product?.kitchen_name ?? '').trim() && String(product?.product_sku ?? '').trim()
  );

  const baselineType = coerceToAllowedStatusType(product?.status_type);
  const baselineNotes = String(product?.status_notes ?? '');

  const unchanged =
    selectedStatusType === baselineType && statusNotes === baselineNotes;

  const handleSubmit = async () => {
    if (!hasIdentity || unchanged) return;
    setFormErrors([]);

    const kitchen = String(product?.kitchen_name ?? '')
      .trim()
      .toLowerCase();
    const sku = String(product?.product_sku ?? '').trim();
    const payload = {
      kitchen_name: kitchen,
      product_sku: sku,
      status_type: selectedStatusType,
      status_notes: statusNotes.trim() === '' ? null : statusNotes.trim(),
    };

    const managing = getManagingBranchNameForApi();
    if (managing != null && String(managing).trim() !== '') {
      payload.managing_branch_name = String(managing)
        .trim()
        .toLowerCase()
        .slice(0, 50);
    }

    const changedByName =
      String(product?.kitchen_name ?? '').trim() ||
      (managing != null ? String(managing).trim() : '') ||
      'regional';
    payload.user_account = { name: changedByName };

    const res = await regionalUpdateProductStatus(payload);
    if (res?.hasError) {
      setFormErrors([
        { errorMessage: res.message || 'Failed to update product status.', param: 'status_update' },
      ]);
      return;
    }
    const body = res?.data ?? {};
    if (body.success === true) {
      setSuccessOpen(true);
      setStatusNotes(String(body.product?.status_notes ?? payload.status_notes ?? ''));
      if (body.product?.status_type != null) {
        setSelectedStatusType(coerceToAllowedStatusType(body.product.status_type));
      }
      onRefresh?.();
      return;
    }
    setFormErrors(mapRegionalProductUpdateErrorsToForm(body));
  };

  const statusChip = <Chip label={typeLabel} size="small" sx={chipSx(typeColor)} />;

  return (
    <SectionCard title="Product status (regional)" style={statusSectionCardStyle}>
      <div className={styles.statusCardBody}>
        {formErrors.length > 0 && (
          <Box sx={{ mb: 1.5, '& > div': { width: '100%', maxWidth: '100%' } }}>
            <ErrorFormAlert data={formErrors} fullWidth />
          </Box>
        )}

        <div className={styles.statusBadgeRow}>
          <span className={styles.fieldLabel}>Current workflow status</span>
          {statusChip}
        </div>

        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl
            fullWidth
            size="small"
            sx={{
              width: '100%',
              ...storeManagementOutlinedSelectSx,
              '& .MuiOutlinedInput-root .MuiSelect-select': {
                textTransform: 'none',
              },
            }}
          >
            <InputLabel id="regional-product-status-type-label">Status type</InputLabel>
            <Select
              labelId="regional-product-status-type-label"
              id="regional-product-status-type"
              label="Status type"
              value={selectedStatusType}
              onChange={(e) => setSelectedStatusType(e.target.value)}
              MenuProps={{ PaperProps: { sx: storeManagementMenuPaperSx } }}
            >
              {PRODUCTS_LIST_STATUS_TYPE_VALUES.map((value) => (
                <MenuItem key={value} value={value} sx={storeManagementMenuItemPlainSx}>
                  {statusTypeLabel[value] || value.replace(/_/g, ' ')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Status notes"
            placeholder="Optional — max 500 characters"
            value={statusNotes}
            onChange={(e) => {
              const next = e.target.value;
              if (next.length <= STATUS_NOTES_MAX) setStatusNotes(next);
            }}
            inputProps={{ maxLength: STATUS_NOTES_MAX }}
            helperText={`${statusNotes.length}/${STATUS_NOTES_MAX}`}
            variant="outlined"
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '13px',
                fontFamily: 'Inter Regular',
                backgroundColor: '#fff',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#e0e0e8',
              },
            }}
          />
        </Box>

        <div className={styles.statusButtonRow}>
          <AtomicButton
            label="Update product status"
            handleSubmit={handleSubmit}
            style={{ width: '100%', height: '48px', marginTop: 8, marginBottom: 0 }}
            loadingButtonStyle={{
              backgroundColor: '#e8e8e8',
              color: '#707070',
              border: '1px solid #d0d0d0',
              cursor: 'not-allowed',
            }}
            loading={statusUpdateLoading}
            disabled={statusUpdateLoading || !hasIdentity || unchanged}
          />
        </div>
      </div>

      <Snackbar
        open={successOpen}
        autoHideDuration={5000}
        onClose={() => setSuccessOpen(false)}
        message="Product status updated successfully."
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        className="order-snackbar"
      />
    </SectionCard>
  );
};
